import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from '@shared/customValidators';
import { collectFormErrors } from '@shared/formHelpers';
import { Applicant, ApplicationSegment, CheckVerificationCodeRequestDTO, Company, EmailVerificationData } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { RegistrationService } from '@shared/services/registration.service';
import { SoldoCookieService } from '@shared/services/soldo-cookie.service';
import { APP_CONFIG, AppConfig } from '@src/app/app-config.module';
import { NGXLogger } from 'ngx-logger';

const SUBSTEP_NAME = "Email Verification";
@Component({
    selector: 'email-verification',
    templateUrl: './email-verification.component.html',
    styleUrls: ['./email-verification.component.scss']
})
export class EmailVerificationComponent implements OnInit {
    @ViewChild('codeInput') codeInput: ElementRef<HTMLInputElement>;
    private readonly DOMAIN_NOT_ALLOWED_STATUS_CODE = 452;
    private readonly CODE_INVALID_STATUS_CODE = 453;
    private readonly TOO_MANY_REQUESTS = 429;
    private applicant: Applicant;
    private company: Company;
    private oneTimeCode: string;
    private codeSubmitDtrumActionId: any;
    form: FormGroup;
    loading = false;
    codeLength = 4;
    resendCodeEnabled = true;
    verificationCodeMismatch = false;
    oneTimeCodeVerification = false;
    privacyLink = "-- link --";
    isDirectSales = false;

    constructor(
        @Inject(APP_CONFIG) private readonly config: AppConfig,
        private readonly fb: FormBuilder,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly translateService: TranslateService,
        private readonly registrationService: RegistrationService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService,
        private readonly soldoCookieService: SoldoCookieService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const application = this.route.snapshot.data.application;
        this.applicant = application?.applicant;
        this.company = application?.company;
        this.initForm(this.applicant?.email);
        const oneTimeVerificationData = this.registrationService.getOneTimeCode();
        if (oneTimeVerificationData) {
            this.oneTimeCodeVerification = true;
            this.form.controls.email.setValue(oneTimeVerificationData.email);
            this.oneTimeCode = oneTimeVerificationData.code;
        }
        this.isDirectSales = this.registrationService.getSegment() === ApplicationSegment.direct;

        const pendingEmail = this.soldoCookieService.readEmailVerificationRequestCookie();
        if (pendingEmail) {
            this.form.controls.email.setValue(pendingEmail);
            this.resendCodeEnabled = false;
            this.showCodeField();
            setTimeout(() => {
                this.resendCodeEnabled = true;
            }, 30 * 1000);
        }
    }

    private initForm(email = '') {
        this.form = this.fb.group({
            "email": [email, [
                Validators.required,
                CustomValidators.emailValidatorFactory(),
                Validators.maxLength(CustomValidators.getContactMaxlength('email')),
                CustomValidators.noSoldoPecAddressValidatorFactory()
            ]]
        });
        this.setPrivacyLink(this.translateService.getCurrentLang());
        this.translateService.onLangChange.subscribe(e => {
            this.setPrivacyLink(e.lang);
        });
    }

    private setPrivacyLink(lang: string): void {
        if (this.config.privacyPolicyLink[lang]) {
            this.privacyLink = this.config.privacyPolicyLink[lang];
        } else {
            this.privacyLink = this.config.privacyPolicyLink["en"];
        }
    }

    async requestCode() {
        if (this.resendCodeEnabled) {

            const emailVerificationData: EmailVerificationData = {
                email: this.getEmail(),
                lang: this.translateService.getCurrentLang()
            };
            if (this.applicant) {
                emailVerificationData.firstName = this.applicant.firstName;
                emailVerificationData.lastName = this.applicant.lastName;
            }
            if (this.company) {
                emailVerificationData.countryIso3 = this.company.registeredAddress?.countryISO;
            }

            this.loading = true;
            this.trackCodeRequested();
            const codeRequestActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.EMAIL_VERIFICATION_CODE_REQUEST);
            try {
                await this.registrationService.requestEmailVerificationCode(emailVerificationData);
                this.soldoCookieService.storeEmailVerificationRequestCookie(this.getEmail());
                this.showCodeField();
                this.resendCodeEnabled = false;
                setTimeout(() => {
                    this.resendCodeEnabled = true;
                }, 30 * 1000);
            } catch (error) {
                if (error.status === this.TOO_MANY_REQUESTS) {
                    this.showCodeField();
                    this.soldoCookieService.storeEmailVerificationRequestCookie(this.getEmail());
                } else {
                    this.resendCodeEnabled = true;
                }
                if (error.status === this.DOMAIN_NOT_ALLOWED_STATUS_CODE) {
                    this.form.get("email").setErrors({
                        blacklistedDomain: true
                    });
                    this.dynatrace.setActionName(codeRequestActionId, DTRUM_ACTION_NAME.EMAIL_VERIFICATION_REJECTED);
                    this.trackFreeEmailDomainErrorTriggered(this.form.get("email").value);
                }
            }

            this.dynatrace.leaveAction(codeRequestActionId);
            this.loading = false;
        }
    }

    private showCodeField() {
        if (!this.form.controls.code) {
            this.form.addControl("code", new FormControl(null, [
                Validators.required,
                Validators.pattern(/^\d{4}$/),
                (): ValidationErrors | null => {
                    if (this.verificationCodeMismatch) {
                        return {
                            "codeMismatch": true
                        };
                    } else {
                        return null;
                    }
                }
            ]));
            setTimeout(() => this.codeInput?.nativeElement.focus());
        }
    }

    private removeCodeField(): void {
        this.form.removeControl("code");
    }

    isValid(): boolean {
        this.form.get("email").markAsTouched();

        if (this.form.get("code")) {
            this.form.get("code").markAsTouched();
        }
        return this.form.valid;
    }

    private getEmail(): string {
        return this.form.value["email"];
    }

    private getVerificationRequestObject(): CheckVerificationCodeRequestDTO {
        const formValues = this.form.value;
        return {
            email: formValues["email"],
            code: formValues["code"]
        };
    }

    handleNextStep() {
        if (this.oneTimeCodeVerification) {
            this.verifyOneTimeCode();
        } else {
            this.nextStep();
        }
    }

    async nextStep() {
        if (this.isValid()) {
            if (this.registrationService.getApplicationId() && this.applicant.email === this.form.value.email) {
                this.router.navigate([this.registrationService.getNextStep()]);
            } else if (this.form.get('code')) {
                await this.verifyCode();
            } else {
                this.requestCode();
            }
        } else {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
        }
    }

    private async verifyCode() {
        this.loading = true;
        const emailVerificationData = this.getVerificationRequestObject();

        this.codeSubmitDtrumActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.EMAIL_VERIFICATION_CODE_SUBMIT);

        try {
            await this.registrationService.checkVerificationCode(emailVerificationData);
            await this.loadApplicationsAndProceed(emailVerificationData);
        } catch (error) {
            if (error.status === this.CODE_INVALID_STATUS_CODE) {
                this.verificationCodeMismatch = true;
                this.form.get("code").updateValueAndValidity();
            }

            this.dynatrace.leaveAction(this.codeSubmitDtrumActionId);
            this.loading = false;
        }
    }

    async verifyOneTimeCode() {
        this.loading = true;
        const emailVerificationData = {
            email: this.getEmail(),
            code: this.oneTimeCode
        };

        this.codeSubmitDtrumActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.EMAIL_VERIFICATION_CODE_SUBMIT);

        try {
            await this.registrationService.checkVerificationCode(emailVerificationData);
            await this.loadApplicationsAndProceed(emailVerificationData, true);
        } catch (error) {
            if (error.status === this.CODE_INVALID_STATUS_CODE) {
                this.amplitudeService.trackOneTimeCodeRejected();
                this.dynatrace.leaveAction(this.codeSubmitDtrumActionId);

                await this.requestCode();
                this.oneTimeCodeVerification = false;
            }
            this.loading = false;
        }
    }

    private async loadApplicationsAndProceed(emailVerificationData: CheckVerificationCodeRequestDTO, fastVerification = false) {
        this.amplitudeService.trackEmailVerified(fastVerification);
        this.gtmService.trackEmailVerified();
        const list = await this.registrationService.getApplicationList();
        if (list.length > 0) {
            this.router.navigate(['auth/applications'])
                .then(() => {
                    this.dynatrace.leaveAction(this.codeSubmitDtrumActionId);
                });
        } else {
            this.startNewApplication(emailVerificationData.email)
                .then(() => {
                    this.dynatrace.leaveAction(this.codeSubmitDtrumActionId);
                });
        }
    }

    private startNewApplication(email: string): Promise<void> {
        return this.registrationService.startApplication({
            email: email,
            browserLanguage: this.translateService.getCurrentLang()
        }).then(async (application) => {
            this.trackSubStepCompleted();
            setTimeout(() => {
                window.scrollTo({ top: 0 });
            }, 0);

            const route = await this.registrationService.initRegistration(application);
            this.gtmService.trackNewRegistrationStarted(application.planName, application.offerName, application.segment);
            this.amplitudeService.trackNewRegistrationStarted(application.segment);

            this.router.navigate([route]);
        }).catch((error) => {
            this.logger.trace('FATAL: ', error);
            this.loading = false;
        });
    }

    onEmailChange(): void {
        this.resendCodeEnabled = true;
        this.removeCodeField();
    }

    onCodeInput() {
        this.verificationCodeMismatch = false;
    }

    private trackCodeRequested(): void {
        this.amplitudeService.trackEmailVerificationCodeRequested();
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(SUBSTEP_NAME);
        this.gtmService.trackSubStepViewed(SUBSTEP_NAME);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(SUBSTEP_NAME);
        this.gtmService.trackSubStepCompleted(SUBSTEP_NAME);
    }

    private trackFreeEmailDomainErrorTriggered(email: string): void {
        let domain = "unknown";
        if (email && email !== "" && email.includes("@")) {
            domain = email.substring(email.lastIndexOf("@") + 1);
        }
        this.amplitudeService.trackFreeEmailDomainErrorTriggered(domain);
    }

}
